import type { FC } from "react";
import { useCallback, useState, useMemo, useEffect } from "react";
import {
    FooterButton,
    ImportFileLink,
    ScrollAreaWrapper,
    TabInnerContentContainer,
    BooleanSearchWrapStyled,
    BooleanSearchContainer,
    ChipPlaceholderLoaderWrapper,
    LoadersWrapper,
    NoDataContainer,
    TextAlign,
} from "./segmentRulesStyles";
import { i18nFilter } from "app/filters/i18nFilter";
import type { IRule, ISegmentFilter } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { ChipContainer } from "@similarweb/ui-components";
import { BooleanSearchInputWrap, BooleanSearchInput } from "@similarweb/ui-components";
import { ChipWrapper } from "pro-features/components/Rule/src/styledComponents";
import { AddStringChip } from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleComponents/AddStringChip";
import { SWReactIcons } from "@similarweb/icons";
import { ImportCSVModal } from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModals/ImportCSVModal";
import { RuleSelectedValuesRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleSelectedValuesRow";
import { addItems, removeItems } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import { ButtonPlaceholderLoader } from "@similarweb/ui-components";
import { allTrackers } from "app/services/track/track";

const BASE_SIZE_UNIT = 8;
const NUMBER_OF_LOADERS = 38;
const MAX_NUMBER_OF_SHOWN_CHIPS = 200;

const generateChips = (
    chipsString: string[],
    alreadyChosenStrings: string[],
    onAddWords: (words: string[]) => void,
) => {
    return chipsString
        .filter((elm) => !alreadyChosenStrings.includes(elm))
        .slice(0, MAX_NUMBER_OF_SHOWN_CHIPS)
        .map((elm) => ({
            text: elm,
            key: `seg-chip-${elm.split(" ").join("-")}`,
            onCloseItem: onAddWords.bind(null, [elm], false),
            displayTooltipForTextLongerThan: -1,
        }));
};

const generateRandomSize = (max, baseSizeUnit = BASE_SIZE_UNIT) => {
    const minWidth = 7 * baseSizeUnit;
    return baseSizeUnit * Math.floor(Math.random() * Math.floor(max)) + minWidth;
};

const generatePlaceholderLoaders = (num: number) => {
    const loaders = [];
    for (let i = 0; i <= num; i++) {
        loaders.push(
            <ChipPlaceholderLoaderWrapper
                key={i}
                style={{ width: generateRandomSize(5) }}
            >
                <ButtonPlaceholderLoader />
            </ChipPlaceholderLoaderWrapper>,
        );
    }

    return loaders;
};

const filterWords = (filter, words) => {
    filter = filter.toLowerCase();
    return filter.trim().length > 0 ? words.filter((word) => word.toLowerCase().includes(filter)) : words;
};

export const StringsFilter: FC<ISegmentFilter> = ({ rule, onApply, filterType, selectedTab }) => {
    const [currentRule, setCurrentRule] = useState<IRule>(null);
    const [searchWordsFilter, setSearchWordsFilter] = useState<string>("");
    const [isOpenImportModal, setIsOpenImportModal] = useState(false);
    const { wordPredictions, wordPredictionsLoading } = useSegmentWizardContext();

    useEffect(() => {
        const componentRule = { ...rule };
        // Spread is a shallow copy, and I want to fully copy only the words,
        // as the rest of the rule filters will remain unchanged while editing the current rule
        componentRule.words = [...rule.words];
        setCurrentRule(componentRule);
    }, []);

    const wordPredictionsStrings = wordPredictions?.Data ?? [];

    const onSearchChange = useCallback(
        (evt) => {
            setSearchWordsFilter(evt.target.value);
        },
        [setSearchWordsFilter],
    );

    const onApplyFilter = useCallback(
        () => onApply(currentRule, filterType, selectedTab),
        [onApply, currentRule, filterType, selectedTab],
    );

    const onAddWords = useCallback(
        (words: string[]) => {
            const updatedRule = addItems({ words }, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const validateCSVWords = useCallback(
        (uploadedStrings: string[]): string[] => {
            return uploadedStrings.filter((word) => wordPredictionsStrings.includes(word.toLowerCase()));
        },
        [wordPredictionsStrings],
    );

    const onRemoveWords = useCallback(
        (words: string[]) => {
            const updatedRule = removeItems("words", words, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const handleDownloadSampleCsv = useCallback(() => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            "segments/wizard/new/stringTab/upload/modal/download/csv/sample",
        );
    }, []);

    const handleImportStrings = useCallback(
        (strings) => {
            onAddWords(strings);
        },
        [onAddWords],
    );

    const openImportFileModal = useCallback(() => {
        setIsOpenImportModal(true);
    }, []);

    const closeImportFileModal = useCallback(() => {
        setIsOpenImportModal(false);
    }, []);

    const filteredWords = filterWords(searchWordsFilter, wordPredictionsStrings);
    const chips = useMemo(() => {
        if (wordPredictionsLoading) {
            return <LoadersWrapper>{generatePlaceholderLoaders(NUMBER_OF_LOADERS)}</LoadersWrapper>;
        }
        const wordsForChips = filterWords(searchWordsFilter, wordPredictionsStrings);
        const selectedWords = currentRule ? currentRule.words : [];
        return (
            <ChipWrapper>
                <ChipContainer itemsComponent={AddStringChip}>
                    {generateChips(wordsForChips, selectedWords, onAddWords)}
                </ChipContainer>
            </ChipWrapper>
        );
    }, [wordPredictionsLoading, wordPredictionsStrings, searchWordsFilter, currentRule?.words, onAddWords]);

    const renderNoSearchData = () => {
        return (
            <NoDataContainer>
                <TextAlign>{i18nFilter()("segmentWizard.editRule.string.no.results.part1")}</TextAlign>
                <TextAlign>{i18nFilter()("segmentWizard.editRule.string.no.results.part2")}</TextAlign>
            </NoDataContainer>
        );
    };

    return (
        <>
            <TabInnerContentContainer>
                <ImportCSVModal
                    warningText={"segmentWizard.csvModal.error.wrong.rule"}
                    validateCSVWords={validateCSVWords}
                    isOpen={isOpenImportModal}
                    onClose={closeImportFileModal}
                    onImportSuccess={handleImportStrings}
                    onDownloadSampleCsvClick={handleDownloadSampleCsv}
                    modalTitle={"segmentWizard.csvModal.title.import.strings"}
                />
                {currentRule?.words?.length > 0 && (
                    <RuleSelectedValuesRow
                        isFilterMode={true}
                        title={i18nFilter()("segmentWizard.editRule.string.row.title")}
                        valuesList={currentRule.words}
                        onRemoveValues={onRemoveWords}
                    />
                )}
                <BooleanSearchContainer>
                    <BooleanSearchWrapStyled showBorderBottom={true}>
                        <SWReactIcons
                            size="sm"
                            iconName={"search"}
                        />
                        <BooleanSearchInputWrap>
                            <BooleanSearchInput
                                value={searchWordsFilter}
                                onChange={onSearchChange}
                                placeholder={i18nFilter()(
                                    "workspaces.redesigned.segment.wizard.input_search.placeholder",
                                )}
                                autoFocus={true}
                            />
                        </BooleanSearchInputWrap>
                        <ImportFileLink
                            onClick={openImportFileModal}
                            type="outlined"
                            iconName="download"
                        >
                            {i18nFilter()("segmentWizard.redesigned.editRule.strings.modal.button.importStrings")}
                        </ImportFileLink>
                    </BooleanSearchWrapStyled>
                </BooleanSearchContainer>
                {searchWordsFilter && filteredWords.length === 0 ? (
                    renderNoSearchData()
                ) : (
                    <ScrollAreaWrapper>{chips}</ScrollAreaWrapper>
                )}
            </TabInnerContentContainer>
            <FooterButton
                onClick={onApplyFilter}
                isEnabled={currentRule?.words?.length > 0}
            >
                {i18nFilter()("common.apply")}
            </FooterButton>
        </>
    );
};
